// Copyright (c) 2015-present Mattermost, Inc. All Rights Reserved.
// See LICENSE.txt for license information.

import React from 'react';
import {defineMessage, useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';

import {getSubscriptionProduct} from 'mattermost-redux/selectors/entities/cloud';

import {closeModal} from 'actions/views/modals';

import useGetHighestThresholdCloudLimit from 'components/common/hooks/useGetHighestThresholdCloudLimit';
import useGetLimits from 'components/common/hooks/useGetLimits';
import useGetUsage from 'components/common/hooks/useGetUsage';
import useOpenPricingModal from 'components/common/hooks/useOpenPricingModal';

import {ModalIdentifiers} from 'utils/constants';
import type {Message} from 'utils/i18n';
import {fallbackStarterLimits, asGBString, LimitTypes} from 'utils/limits';

import CloudUsageModal from './index';

export default function LHSNearingLimitsModal() {
    const dispatch = useDispatch();
    const product = useSelector(getSubscriptionProduct);
    const usage = useGetUsage();
    const intl = useIntl();
    const {openPricingModal, isAirGapped} = useOpenPricingModal();

    const [limits] = useGetLimits();

    const primaryAction = isAirGapped ? undefined : {
        message: defineMessage({
            id: 'workspace_limits.modals.view_plans',
            defaultMessage: 'View plans',
        }),
        onClick: () => openPricingModal(),
    };
    const secondaryAction = {
        message: defineMessage({
            id: 'workspace_limits.modals.close',
            defaultMessage: 'Close',
        }),
        onClick: () => {
            dispatch(closeModal(ModalIdentifiers.CLOUD_LIMITS));
        },
    };
    const highestLimit = useGetHighestThresholdCloudLimit(usage, limits);
    let title: Message = defineMessage({
        id: 'workspace_limits.modals.informational.title',
        // eslint-disable-next-line formatjs/enforce-placeholders -- planName provided via values property
        defaultMessage: '{planName} limits',
        values: {
            planName: product?.name,
        },
    });

    let description: Message = defineMessage({
        id: 'workspace_limits.modals.informational.description.freeLimits',
        // eslint-disable-next-line formatjs/enforce-placeholders -- planName, messages, storage provided via values property
        defaultMessage: '{planName} is restricted to {messages} message history and {storage} file storage.',
        values: {
            planName: product?.name,
            messages: intl.formatNumber(limits?.messages?.history ?? fallbackStarterLimits.messages.history),
            storage: asGBString(limits?.files?.total_storage ?? fallbackStarterLimits.files.totalStorage, intl.formatNumber),
        },
    });

    if (highestLimit && highestLimit.id === LimitTypes.messageHistory) {
        title = defineMessage({
            id: 'workspace_limits.modals.limits_reached.title.message_history',
            defaultMessage: 'Message history',
        });

        description = defineMessage({
            id: 'workspace_limits.modals.limits_reached.description.message_history',
            defaultMessage: 'Your sent message history is no longer available but you can still send messages. Upgrade to a paid plan and get unlimited access to your message history.',
        });
    }

    return (
        <CloudUsageModal
            title={title}
            description={description}
            primaryAction={primaryAction}
            secondaryAction={secondaryAction}
            onClose={() => {
                dispatch(closeModal(ModalIdentifiers.CLOUD_LIMITS));
            }}
        />
    );
}
